import React, { useMemo } from 'react';

import { useDateRefresh } from '@gitbook/util-react-hooks';

import { toLocaleDateTimeString } from '../Intl';

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const MONTH = 31 * DAY;
const YEAR = 365 * DAY;

/**
 * Hook to use a relative date. Handles SSR refreshing and any locale management.
 */
export function useDateShortRelative(
    input: Date | string,
    options: { onlyDate?: boolean } = { onlyDate: true }
): {
    /** The date value */
    date: Date;
    /** A human-readable relative value for the date */
    value: string;
    /** An accessible value for the date */
    accessibilityValue: string;
    /** A tooltip for the date */
    tooltip: string;
} {
    const { onlyDate = true } = options;

    const value = React.useMemo(() => new Date(input), [input]);
    useDateRefresh(value);

    const readableValue = React.useMemo(
        () =>
            value.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
            }),
        [value]
    );
    const humanPeriod = getHumanPeriod(value, onlyDate);
    const tooltip = useMemo(() => toLocaleDateTimeString(value), [value]);

    return { date: value, value: humanPeriod, accessibilityValue: readableValue, tooltip };
}

function getHumanPeriod(value: Date, onlyDate: boolean) {
    const timestampNow = process.env.GITBOOK_PREDICTABLE_UI
        ? new Date(2021, 1, 1).getTime()
        : Date.now();

    const period = Math.max(1000, Math.abs(timestampNow - value.getTime()));
    const [years, months, days, hours, minutes] = [
        period / YEAR,
        period / MONTH,
        period / DAY,
        period / HOUR,
        period / MINUTE,
    ].map((n) => Math.floor(n));
    const isFuture = value.getTime() > timestampNow;

    const prefix = !onlyDate && isFuture ? 'in ' : '';
    const suffix = !onlyDate && !isFuture ? ' ago' : '';

    if (years >= 1) {
        return `${prefix}${years}yr${suffix}`;
    }
    if (months >= 1) {
        return `${prefix}${months}mo${suffix}`;
    }
    if (days >= 1) {
        return `${prefix}${days}d${suffix}`;
    }
    if (hours >= 1) {
        return `${prefix}${hours}h${suffix}`;
    }
    if (minutes >= 1) {
        return `${prefix}${minutes}m${suffix}`;
    }
    return 'now';
}
